import styled, { css } from 'styled-components';

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  align-self: flex-start;
  font-family: inherit;
  transition: color 0.15s ease;

  &:hover {
    color: #006633;
  }
`;

export const Card = styled.section`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  overflow: hidden;
`;

export const ProfileHeader = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 20px;
  padding: 22px;

  @media (max-width: 768px) {
    grid-template-columns: auto 1fr;

    .header-actions {
      grid-column: 1 / -1;
      justify-content: flex-start;
    }
  }
`;

export const Avatar = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 18px;
  background: linear-gradient(135deg, #006633 0%, #00874a 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
  box-shadow: 0 6px 18px rgba(0, 102, 51, 0.18);
  flex-shrink: 0;
`;

export const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1.15;
  }

  .razao {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    font-size: 13px;
    color: #64748b;

    span {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    svg {
      color: #94a3b8;
    }
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  font-family: inherit;
  white-space: nowrap;
`;

export const GhostButton = styled.button`
  ${buttonBase}
  background: #ffffff;
  color: #334155;
  border: 1px solid #e2e8f0;

  &:hover {
    background: #f8fafc;
    border-color: #006633;
    color: #006633;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: #006633;
  color: #ffffff;
  border: 1px solid transparent;

  &:hover {
    background: #00451f;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const DangerButton = styled.button`
  ${buttonBase}
  background: #ffffff;
  color: #b91c1c;
  border: 1px solid #fecaca;

  &:hover {
    background: #fef2f2;
    border-color: #b91c1c;
  }
`;

export const SmallAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    ${({ $danger }) =>
      $danger
        ? css`
            background: #fef2f2;
            border-color: #b91c1c;
            color: #b91c1c;
          `
        : css`
            background: #f8fafc;
            border-color: #006633;
            color: #006633;
          `}
  }
`;

/* ─── Stats row ─── */

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

type StatTone = 'brand' | 'info' | 'warning' | 'success' | 'neutral';
const STAT_TONES: Record<StatTone, { iconBg: string; iconColor: string }> = {
  brand: { iconBg: '#e6f3ec', iconColor: '#006633' },
  info: { iconBg: '#e0ecff', iconColor: '#1d4ed8' },
  warning: { iconBg: '#fff4e0', iconColor: '#b45309' },
  success: { iconBg: '#dcfce7', iconColor: '#166534' },
  neutral: { iconBg: '#f1f5f9', iconColor: '#475569' },
};

export const StatCard = styled.div<{ $tone: StatTone }>`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 14px;

  .icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    ${({ $tone }) => css`
      background: ${STAT_TONES[$tone].iconBg};
      color: ${STAT_TONES[$tone].iconColor};
    `}
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .value {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }
`;

/* ─── Tabs ─── */

export const Tabs = styled.div`
  display: flex;
  gap: 6px;
  border-bottom: 1px solid #eef2f7;
  padding: 4px 22px 0;
  background: #ffffff;
`;

export const TabButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  padding: 12px 14px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  color: ${({ $active }) => ($active ? '#006633' : '#64748b')};
  border-bottom: 2px solid
    ${({ $active }) => ($active ? '#006633' : 'transparent')};
  transition: color 0.15s ease, border-color 0.15s ease;
  margin-bottom: -1px;

  &:hover {
    color: #006633;
  }

  .count {
    background: ${({ $active }) => ($active ? '#e6f3ec' : '#f1f5f9')};
    color: ${({ $active }) => ($active ? '#006633' : '#64748b')};
    font-size: 11px;
    font-weight: 700;
    padding: 1px 7px;
    border-radius: 999px;
    font-variant-numeric: tabular-nums;
  }
`;

/* ─── Sections ─── */

export const SectionHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 22px;
  border-bottom: 1px solid #eef2f7;
  background: #fbfcfe;

  h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  svg {
    color: #006633;
  }

  .right {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
`;

export const SectionBody = styled.div`
  padding: 20px 22px;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px 28px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  grid-column: span ${({ $span }) => $span || 1};

  @media (max-width: 992px) {
    grid-column: span ${({ $span }) => Math.min($span || 1, 2)};
  }

  @media (max-width: 576px) {
    grid-column: span 1;
  }

  .label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .value {
    font-size: 14px;
    color: #0f172a;
    font-weight: 500;
    min-height: 20px;
    word-break: break-word;

    &.empty {
      color: #cbd5e1;
      font-style: italic;
      font-weight: 400;
    }
  }
`;

/* ─── Empty state ─── */

export const EmptyState = styled.div`
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  text-align: center;

  .icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: #f1f5f9;
    color: #94a3b8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
  }

  .title {
    font-size: 14px;
    font-weight: 600;
    color: #475569;
  }

  .desc {
    font-size: 12.5px;
    font-weight: 500;
    color: #94a3b8;
    max-width: 340px;
  }
`;

/* ─── Filiais (cities) list ─── */

export const CityList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CityItem = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 14px 22px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fbfcfe;
  }

  .icon {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: #e0ecff;
    color: #1d4ed8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .name {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meta {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 12.5px;
    color: #64748b;

    span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    svg {
      color: #94a3b8;
    }
  }

  .actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }
`;

/* ─── Complaints list ─── */

export const ComplaintList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ComplaintItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  align-items: center;
  gap: 14px;
  padding: 14px 22px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fbfcfe;
  }

  .protocol {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .protocol-num {
    font-size: 13.5px;
    font-weight: 700;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }

  .meta {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: #64748b;

    span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    svg {
      color: #94a3b8;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

type StatusTone = 'success' | 'warning' | 'info' | 'neutral';
const STATUS_TONES: Record<StatusTone, { bg: string; color: string }> = {
  success: { bg: '#e6f3ec', color: '#006633' },
  warning: { bg: '#fff4e0', color: '#b45309' },
  info: { bg: '#e0ecff', color: '#1d4ed8' },
  neutral: { bg: '#f1f5f9', color: '#475569' },
};

export const StatusChip = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  ${({ $tone }) => css`
    background: ${STATUS_TONES[$tone].bg};
    color: ${STATUS_TONES[$tone].color};
  `}

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

/* ─── Modals (form fields) ─── */

export const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const FieldGroup = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  input,
  select {
    height: 40px;
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    color: #0f172a;
    background: #ffffff;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
    }

    &:focus {
      border-color: #006633;
      box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
    }

    &::placeholder {
      color: #94a3b8;
    }
  }

  .error {
    color: #b91c1c;
    font-size: 12px;
    font-weight: 500;
  }
`;

export const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #006633;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-bottom: 4px;
  border-bottom: 1px solid #eef2f7;
`;

export const ModalNote = styled.p`
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  color: #475569;
  line-height: 1.5;

  strong {
    color: #0f172a;
  }
`;
